import { Box, Grid } from '@mui/material';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { savePDF } from '@progress/kendo-react-pdf';
import bwipjs from 'bwip-js';
import { add, format } from 'date-fns';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from 'src/assets/images/logos/washingtonmills_logo.png';
import { PrimaryButton } from 'src/components/shared/Button/Button';
import { Divider } from 'src/components/shared/Divider/Divider';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ProductDetailDto } from 'src/store/products/product';
import styled from 'styled-components';

type LabelConfig = {
  STMICROELECTRONICS_MATERIAL_CODE: string | undefined;
  STMICROELECTRONICS_DRAWING_NUMBER: string | undefined;
  STMICROELECTRONICS_SUPPLIER_CODE: string | undefined;
  STMICROELECTRONICS_SUPPLIER_PLANT: string | undefined;
  STMICROELECTRONICS_ORDER_NUMBER: string | undefined;
  MPS_DOC: string | undefined;
  MPS_VER: string | undefined;
  PART_M: string | undefined;
};

const LabelConfig1: LabelConfig = {
  STMICROELECTRONICS_MATERIAL_CODE:
    process.env.REACT_APP_STMICROELECTRONICS_MATERIAL_CODE_1,
  STMICROELECTRONICS_DRAWING_NUMBER:
    process.env.REACT_APP_STMICROELECTRONICS_DRAWING_NUMBER_1,
  STMICROELECTRONICS_SUPPLIER_CODE:
    process.env.REACT_APP_STMICROELECTRONICS_SUPPLIER_CODE,
  STMICROELECTRONICS_SUPPLIER_PLANT:
    process.env.REACT_APP_STMICROELECTRONICS_SUPPLIER_PLANT,
  STMICROELECTRONICS_ORDER_NUMBER:
    process.env.REACT_APP_STMICROELECTRONICS_ORDER_NUMBER,
  MPS_DOC: process.env.REACT_APP_MPS_DOC,
  MPS_VER: process.env.REACT_APP_MPS_VER,
  PART_M: process.env.REACT_APP_PART_M
};

const LabelConfig2: LabelConfig = {
  STMICROELECTRONICS_MATERIAL_CODE:
    process.env.REACT_APP_STMICROELECTRONICS_MATERIAL_CODE_2,
  STMICROELECTRONICS_DRAWING_NUMBER:
    process.env.REACT_APP_STMICROELECTRONICS_DRAWING_NUMBER_2,
  STMICROELECTRONICS_SUPPLIER_CODE:
    process.env.REACT_APP_STMICROELECTRONICS_SUPPLIER_CODE,
  STMICROELECTRONICS_SUPPLIER_PLANT:
    process.env.REACT_APP_STMICROELECTRONICS_SUPPLIER_PLANT,
  STMICROELECTRONICS_ORDER_NUMBER:
    process.env.REACT_APP_STMICROELECTRONICS_ORDER_NUMBER,
  MPS_DOC: process.env.REACT_APP_MPS_DOC,
  MPS_VER: process.env.REACT_APP_MPS_VER,
  PART_M: process.env.REACT_APP_PART_M
};

type CrystalLabelProps = {
  product: ProductDetailDto;
};

export const CrystalLabel: React.FC<CrystalLabelProps> = ({ product }) => {
  const { t } = useTranslation();

  const [orderNo, setOrderNo] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [bigBag, setBigBag] = useState<boolean>(false);
  const [labelConfig, setLabelConfig] = useState<LabelConfig>(LabelConfig1);

  const labelsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLabelConfig(bigBag ? LabelConfig2 : LabelConfig1);
  }, [labelConfig, bigBag]);

  const onPrint = () => {
    setIsPrinting(true);
    savePDF(labelsRef.current as HTMLElement, {
      fileName: 'crystal.pdf',
      forcePageBreak: '.page-break'
    });
    setIsPrinting(false);
  };
  const lotDate = product.lotDate ? new Date(product.lotDate) : new Date();
  const lotDateExp = add(lotDate, { years: 3 });
  const SNLotDateFormat = 'yyyyww';
  const ExpLotDateFormat = 'yyyyMMdd';
  const formattedLotDate = format(lotDate, SNLotDateFormat);

  const PN = `${labelConfig.STMICROELECTRONICS_DRAWING_NUMBER} - ${labelConfig.STMICROELECTRONICS_MATERIAL_CODE}`;
  const BATCH_NO = product.lotNo;
  const EXP = format(lotDateExp, ExpLotDateFormat);

  const MPS_DOC = labelConfig.MPS_DOC;
  const MPS_VER = labelConfig.MPS_VER;
  const PART_M = labelConfig.PART_M;

  const quantityArray = useMemo(
    () => Array.from(Array(quantity ?? 0).keys()).map(val => val + 1),
    [quantity]
  );

  const Labels = quantityArray.map(value => {
    const paddedQuantity = value.toString().padStart(4, '0');
    const SN = `${formattedLotDate}${paddedQuantity}`;
    const UID = `${labelConfig.STMICROELECTRONICS_MATERIAL_CODE}${labelConfig.STMICROELECTRONICS_SUPPLIER_CODE}${labelConfig.STMICROELECTRONICS_SUPPLIER_PLANT}${SN}`;

    return (
      <Fragment key={SN}>
        <LabelWrapper>
          <LabelDataMatrixContainer>
            <canvas id={`data-matrix-${value}`}></canvas>
          </LabelDataMatrixContainer>

          <LabelBody>
            <h2>
              {product.specification.segmentName ?? ''} {product.grade.name}
            </h2>

            <div className="flex jc-sb">
              <ul>
                <li>
                  <b>PN: {PN}</b>
                </li>
                <li>
                  <b>SN: {SN}</b>
                </li>
                <li>
                  <b>Qty: 1</b>
                </li>
              </ul>

              <ul style={{ width: 112 }}>
                <li>Order nr: {orderNo}</li>
                <li>Batch nr: {BATCH_NO}</li>
                <li>Exp: {EXP}</li>
              </ul>
            </div>
            <h5>UID: {UID}</h5>
            <div className="flex jc-sb">
              <ul>
                <li>MPS Doc: {MPS_DOC}</li>
              </ul>

              <ul style={{ width: 112 }}>
                <li>MPS ver: {MPS_VER}</li>
                <li>Part M: {PART_M}</li>
              </ul>
            </div>
          </LabelBody>

          <LabelLogo>
            <img src={logo} alt="LIMS logo" />
          </LabelLogo>
        </LabelWrapper>
        {value < quantity && <div className="page-break" />}
      </Fragment>
    );
  });

  const DM_SUPPLIER_CODE = `SC#${labelConfig.STMICROELECTRONICS_SUPPLIER_CODE}`;
  const DM_SUPPLIER_PLANT = `SP#${labelConfig.STMICROELECTRONICS_SUPPLIER_PLANT}`;
  const DM_ORDERNO = `ORN#${orderNo}`;
  const DM_BATCH_NO = `OBN#${BATCH_NO}`;
  const DM_QUANTITY = `QTY#1`;
  const DM_EXP = `EXP#${EXP}`;
  const DM_STMICROELECTRONICS_MATERIAL_CODE = `STPN#${labelConfig.STMICROELECTRONICS_MATERIAL_CODE}`;
  const DM_STMICROELECTRONICS_DRAWING_NUMBER = `PN#${labelConfig.STMICROELECTRONICS_DRAWING_NUMBER}`;
  const DM_MPS_DOC = `MPSD#${MPS_DOC}`;
  const DM_MPS_VER = `MPSV#${MPS_VER}`;
  const DM_PART_M = `PM#${PART_M}`;

  useEffect(() => {
    if (Labels.length === quantity) {
      quantityArray.forEach(value => {
        const paddedQuantity = value.toString().padStart(4, '0');
        const SN = `${formattedLotDate}${paddedQuantity}`;
        const UID = `${labelConfig.STMICROELECTRONICS_MATERIAL_CODE}${labelConfig.STMICROELECTRONICS_SUPPLIER_CODE}${labelConfig.STMICROELECTRONICS_SUPPLIER_PLANT}${SN}`;
        const DM_SN = `SN#${formattedLotDate}${paddedQuantity}`;
        const DM_UID = `UID#${UID}`;
        const DATA_MATRIX = `${DM_SUPPLIER_CODE}|${DM_SUPPLIER_PLANT}|${DM_ORDERNO}|${DM_BATCH_NO}|${DM_QUANTITY}|${DM_EXP}|${DM_STMICROELECTRONICS_MATERIAL_CODE}|${DM_STMICROELECTRONICS_DRAWING_NUMBER}|${DM_SN}|${DM_UID}|${DM_MPS_DOC}|${DM_MPS_VER}|${DM_PART_M}`;

        bwipjs.toCanvas(
          document.getElementById(`data-matrix-${value}`) as HTMLCanvasElement,
          {
            bcid: 'datamatrix',
            text: DATA_MATRIX,
            scale: 1,
            height: 32, // height in mm
            width: 32, // width in mm
            includetext: false,
            textxalign: 'center'
          }
        );
      });
    }
  }, [
    DM_BATCH_NO,
    DM_EXP,
    DM_MPS_DOC,
    DM_MPS_VER,
    DM_ORDERNO,
    DM_PART_M,
    DM_QUANTITY,
    DM_STMICROELECTRONICS_DRAWING_NUMBER,
    DM_STMICROELECTRONICS_MATERIAL_CODE,
    DM_SUPPLIER_CODE,
    DM_SUPPLIER_PLANT,
    Labels.length,
    formattedLotDate,
    labelConfig.STMICROELECTRONICS_MATERIAL_CODE,
    labelConfig.STMICROELECTRONICS_SUPPLIER_CODE,
    labelConfig.STMICROELECTRONICS_SUPPLIER_PLANT,
    quantity,
    quantityArray
  ]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box display="flex" gap="1.5rem">
          <InputField
            value={orderNo}
            label={t('product.orderNo')}
            onChange={event => {
              setOrderNo(event.target.value);
            }}
          />
          <InputField
            type="number"
            value={quantity}
            label={t('props.count')}
            onChange={event => {
              setQuantity(Number(event.target.value));
            }}
          />
        </Box>

        <Checkbox
          label={t('label.bigbag')}
          name="bigBag"
          checked={bigBag}
          onChange={() => setBigBag(!bigBag)}
        />

        <Divider />

        <LabelsContainer ref={labelsRef}>{Labels}</LabelsContainer>

        <Box display="flex" justifyContent="flex-end" marginTop="5.5rem">
          <PrimaryButton onClick={onPrint} disabled={isPrinting}>
            {t('print')}
          </PrimaryButton>
        </Box>
      </Grid>
    </Grid>
  );
};

const LabelsContainer = styled.div`
  overflow: auto;
  height: 192px;
`;

const LabelWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.3);
  background-color: #fff;
  color: #000;
  border: 1px solid #000;
  width: 101.6mm;
  height: 50.8mm;
  padding: 12px;
`;

export const LabelDataMatrixContainer = styled.div`
  align-self: center;
`;

export const LabelBody = styled.div`
  width: 100%;
  margin-left: 8px;

  h2 {
    font-size: 20px;
    margin: 0;
    margin-bottom: 4px;
  }

  h5 {
    margin: 8px 0;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      font-size: 10px;
      padding: 1px 0;
    }
  }
`;

export const LabelLogo = styled.div`
  position: absolute;
  top: 16px;
  left: 8px;

  img {
    max-width: 25mm;
  }
`;
